import React, { useCallback, useEffect, useState } from 'react';
import feather from 'feather-icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { formatDate, getTransactionClass, getTransactionImage } from '../util/transactionHelper';
import UpdateTransactionBottomSheet from '../components/Modals/UpdateTransactionBottomSheet';
import SuccessToast from '../toast/SuccessToast';
import {
    amountDisplay,
    getUserTimezone,
    getMonthName,
    dateDisplay,
    numberLanguageWise,
} from '../util/helper';
import WarningToast from './WarningToast';
import serverClient from '../api/ServerClient';
import { DeleteToken } from '../auth/Auth';
import Loader from '../components/Loader';
import Alert from '../components/Alert';
import BottomNavbar from '../components/Home/BottomNavbar';
import StatisticsBanner from '../components/Accounts/StatisticsBannerComponent';
import DayWiseLineChart from '../components/Charts/DayWiseLineChart';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import AddEditAccountBottomSheet from '../components/Modals/AddEditAccountBottomSheet';
import BackButton from '../components/BackButton';
import { useLanguage } from '../context/LanguageContext';
import { SERVER_BASE_URL } from '../config/app.config';
import { FiAlertCircle } from 'react-icons/fi';
import AttachmentFilePreview from '../components/Attachments/AttachmentFilePreview';
import { isPreviewableImage } from '../util/attachmentHelper';
import '../styles/AccountDetailsAttachment.css';

const AccountDetails = () => {
    const { language, t } = useLanguage();
    const { id } = useParams();
    const navigate = useNavigate();
    const [transactionsData, setTransactionsData] = useState({});
    const [data, setData] = useState(null);
    const [transaction, setTransaction] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [showToastForAccount, setShowToastForAccount] = useState(false);
    const [showToastForDelete, setShowToastForDelete] = useState(false);
    const [showToastForDeleteAccount, setShowToastForDeleteAccount] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [modalMode, setModalMode] = useState('add');
    const [selectedAccount, setSelectedAccount] = useState(null);
    const [attachmentBroken, setAttachmentBroken] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);

    dayjs.extend(utc);
    dayjs.extend(timezone);
    const currentMonth = dayjs().tz(getUserTimezone()).format('MMM');

    const handleTransactionAction = () => {
        fetchAccountDetails();
    };

    const handleShow = (account = null) => {
        setSelectedAccount(account);
        setModalMode(account ? 'update' : 'add');
        setShowModal(true);
    };
    const handleClose = () => {
        setSelectedAccount(null);
        setShowModal(false);
    };

    const handleAccountAddedOrUpdated = () => {
        fetchAccountDetails();
    };

    const fetchAccountDetails = useCallback(async () => {
        try {
            setLoading(true);
            const response = await serverClient.get(`/api/user/account/${id}`);
            setTransactionsData(response?.data?.transactionDateWise);
            setData(response?.data);
        } catch (e) {
            if (e.response?.status === 401) {
                DeleteToken();
                setError(t('userNotAuthorized'));
                navigate('/login');
            } else if (e.response?.status === 404) {
                navigate('/');
            } else {
                setError(t('somethingWentWrong'));
            }
        } finally {
            setLoading(false);
        }
    }, [id, navigate, t]);

    useEffect(() => {
        feather.replace();
        fetchAccountDetails();
    }, [fetchAccountDetails]);

    useEffect(() => {
        setAttachmentBroken(false);
        setLightboxOpen(false);
    }, [data?.account?.attachment]);

    const attachmentUrl = data?.account?.attachment
        ? `${SERVER_BASE_URL}/images/account_attachments/${data.account.attachment}`
        : null;
    const handleShowBottomSheet = (transaction) => {
        setTransaction(null);
        setShowModal(false);

        setTimeout(() => {
            setTransaction(transaction);
            setShowModal(true);
        }, 10);
    };

    const handleCloseBottomSheet = () => {
        setShowModal(false);
    };

    return (
        <>
            <AddEditAccountBottomSheet
                show={showModal}
                onClose={handleClose}
                onAccountAction={handleAccountAddedOrUpdated}
                mode={modalMode}
                accountData={selectedAccount}
                setShowToast={setShowToastForAccount}
                setShowToastForDelete={setShowToastForDeleteAccount}
            />
            <Loader loading={loading} />
            <header className="section-t-space">
                <div className="custom-container">
                    <div className="header-panel">
                        <BackButton />
                        <h2>{data?.account?.name}</h2>

                        <div className="dropdown">
                            <Link
                                to="#"
                                onClick={() => handleShow(data?.account)}
                                className="back-btn"
                                role="button"
                            >
                                <i className="icon" data-feather="edit"></i>
                            </Link>
                        </div>
                    </div>
                </div>
            </header>

            <DayWiseLineChart
                expenseData={data?.dailyBalanceReport?.map((i) => i.balance_end_of_day) ?? []}
                daysInMonth={data?.daysInMonth ?? 0}
                header={`${t('balance')} | ${getMonthName(currentMonth, t)}`}
                title={t('balance')}
            />

            <StatisticsBanner totalSaving={data?.account?.balance} />

            <DayWiseLineChart
                expenseData={data?.expenseData}
                daysInMonth={data?.daysInMonth}
                header={`${t('expense')} | ${getMonthName(currentMonth, t)}`}
                title={t('expense')}
            />

            <section className="section-b-space">
                <div className="custom-container">
                    <div className="title">
                        <h2>{t('latestTransaction')}</h2>
                        {Object.keys(transactionsData).length > 0 && (
                            <Link to="/transaction-history">{t('seeAll')}</Link>
                        )}
                    </div>
                    <hr />
                    {error && <Alert alert={'danger'}>{error}</Alert>}
                    {Object.keys(transactionsData).length > 0 ? (
                        Object.keys(transactionsData).map((date) => (
                            <div key={date}>
                                <div className="title margin-top-5">
                                    <h2>{dateDisplay(formatDate(date), language)}</h2>
                                </div>
                                <div className="row gy-3">
                                    {transactionsData[date].map((transaction) => {
                                        const localDateString =
                                            transaction.transaction_time.replace('Z', ''); // Remove Z
                                        const bdDate = new Date(localDateString);
                                        const formattedDate = bdDate.toLocaleTimeString('en-US', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            timeZone: getUserTimezone(),
                                        });
                                        return (
                                            <div className="col-12" key={transaction.id}>
                                                <div
                                                    className="transaction-box"
                                                    onClick={() => {
                                                        handleShowBottomSheet(transaction);
                                                    }}
                                                >
                                                    <div className="d-flex gap-3">
                                                        <div className="transaction-image">
                                                            <img
                                                                className="img-fluid transaction-icon"
                                                                src={getTransactionImage(
                                                                    transaction.category_id
                                                                )}
                                                                alt={transaction.transaction_type}
                                                            />
                                                        </div>
                                                        <div className="transaction-details">
                                                            <div className="transaction-name">
                                                                <h5>
                                                                    {transaction.category_name ||
                                                                        'Transfer/Withdraw'}
                                                                </h5>
                                                                <h3
                                                                    className={getTransactionClass(
                                                                        transaction.transaction_type
                                                                    )}
                                                                >
                                                                    {amountDisplay(
                                                                        transaction.amount,
                                                                        language
                                                                    )}
                                                                </h3>
                                                            </div>
                                                            <div className="d-flex justify-content-between">
                                                                <h5 className="light-text">
                                                                    {transaction.category_name ===
                                                                    null
                                                                        ? `${transaction.account_id === 0 ? t('outOfWallet') : transaction.account_name} -> ${transaction.to_account_id === 0 ? t('outOfWallet') : transaction.to_account_name}`
                                                                        : transaction.account_name}
                                                                </h5>
                                                                <h5 className="light-text">
                                                                    {numberLanguageWise(
                                                                        formattedDate,
                                                                        language
                                                                    )}
                                                                </h5>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    ) : !loading ? (
                        <>
                            <div className="empty-page">
                                <img
                                    className="notification-img custom-img-fluid transaction-icon"
                                    src={'/assets/images/svg/empty-v4.svg'}
                                    alt={'No Data'}
                                />
                                <h3 className="d-block fw-normal dark-text text-center mt-3">
                                    {t('noTransaction')}
                                    <p>{t('noTransactionsFound')}</p>
                                </h3>
                            </div>
                        </>
                    ) : null}
                </div>
            </section>

            {attachmentUrl && (
                <section className="section-b-space pt-0">
                    <div className="custom-container">
                        <div className="account-detail-attachment-card">
                            <h2 className="title remarks-label">{t('accountAttachment')}</h2>

                            {attachmentBroken ? (
                                <div
                                    className="account-detail-attachment-fallback"
                                    role="status"
                                    aria-live="polite"
                                >
                                    <FiAlertCircle size={18} />
                                    <span>{t('imageUnavailable')}</span>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className="account-detail-attachment-preview"
                                    onClick={() => {
                                        if (isPreviewableImage(data.account.attachment)) {
                                            setLightboxOpen(true);
                                        } else {
                                            window.open(
                                                attachmentUrl,
                                                '_blank',
                                                'noopener,noreferrer'
                                            );
                                        }
                                    }}
                                    aria-label={t('viewAttachment')}
                                >
                                    <AttachmentFilePreview
                                        url={attachmentUrl}
                                        filename={data.account.attachment}
                                        imgError={attachmentBroken}
                                        onImgError={() => setAttachmentBroken(true)}
                                        className="account-detail-attachment-image"
                                        alt={t('accountAttachment')}
                                        clickable={false}
                                    />
                                </button>
                            )}
                        </div>
                    </div>
                </section>
            )}

            {transaction && (
                <UpdateTransactionBottomSheet
                    show={showModal}
                    onClose={handleCloseBottomSheet}
                    onTransactionUpdate={handleTransactionAction}
                    setShowToast={setShowToast}
                    transaction={transaction}
                    setShowToastForDelete={setShowToastForDelete}
                />
            )}

            {showToast && (
                <SuccessToast
                    message={t('transactionUpdated')}
                    onClose={() => setShowToast(false)}
                />
            )}

            {showToastForAccount && (
                <SuccessToast
                    message={t('accountUpdated')}
                    onClose={() => setShowToastForAccount(false)}
                />
            )}

            {showToastForDelete && (
                <WarningToast
                    icon={'https://cdn-icons-png.flaticon.com/512/6861/6861362.png'}
                    message={t('transactionRemoved')}
                    onClose={() => setShowToastForDelete(false)}
                />
            )}

            {showToastForDeleteAccount && (
                <WarningToast
                    icon={'https://cdn-icons-png.flaticon.com/512/6861/6861362.png'}
                    message={t('accountDeleted')}
                    onClose={() => setShowToastForDeleteAccount(false)}
                />
            )}

            {lightboxOpen && !attachmentBroken && attachmentUrl && (
                <div
                    className="account-detail-attachment-lightbox"
                    onClick={() => setLightboxOpen(false)}
                >
                    <img
                        src={attachmentUrl}
                        alt={t('accountAttachment')}
                        className="lightbox-img"
                    />
                    <button
                        type="button"
                        className="lightbox-close"
                        aria-label={t('close')}
                        onClick={(e) => {
                            e.stopPropagation();
                            setLightboxOpen(false);
                        }}
                    >
                        &times;
                    </button>
                </div>
            )}

            <BottomNavbar />
        </>
    );
};

export default AccountDetails;
